/**
 * Profil — Name/Firma/Rolle, Gesamt-Fortschritt, Schriftgröße, Darstellung,
 * Konto (Passwort ändern, Account löschen — Web-Parität b00159d), Fortschritt
 * zurücksetzen, Abmelden, Zertifikat, Kontingent, (Trainer) Statistik.
 */
import { useEffect, useState } from "preact/hooks";
import { useLocation } from "preact-iso";
import { user, isTrainer, clearSession } from "../state/session";
import { logout } from "../state/auth";
import { endpoints } from "../api/endpoints";
import { ApiError } from "../api/client";
import { themePref, cycleTheme } from "../state/theme";
import {
  fontScalePref,
  setFontScale,
  FONT_SCALE_LABELS,
  FONT_SCALE_SHORT,
  FONT_SCALE_ORDER,
} from "../state/fontscale";
import {
  IconMoon,
  IconRefresh,
  IconLock,
  IconTrash,
  IconLogout,
  IconArrowRight,
} from "../app/icons";
import { doneKeys, resetProgress } from "../state/progress";
import { activeCourseId } from "../data/course-store";
import { clearLastPosition } from "../state/course-nav";
import { resetCoach } from "../state/coach";
import { showToast } from "../ui/toast";
import { Modal } from "../ui/Modal";
import { certificateStatus, loadCertificateStatus, downloadCertificate } from "../state/completion";
import { BrandHeader } from "../app/BrandHeader";
import { QuotaCard } from "../ui/QuotaCard";
import { Stats } from "./Stats";

export function Profile() {
  const { route } = useLocation();
  const u = user.value;

  useEffect(() => {
    void loadCertificateStatus(activeCourseId.value);
  }, []);

  async function onDownloadCertificate() {
    const ok = await downloadCertificate();
    if (!ok) showToast("Urkunde konnte nicht geladen werden.");
  }

  async function onLogout() {
    resetCoach();
    await logout();
    route("/login", true);
  }

  // Fortschritt zurücksetzen — Sicherheitsfrage als Dialog, Standard NEIN (Web-Parität 38c93a8).
  const [resetConfirm, setResetConfirm] = useState(false);
  function onReset() {
    setResetConfirm(true);
  }
  function onResetConfirmed() {
    setResetConfirm(false);
    resetProgress(activeCourseId.value);
    clearLastPosition();
    showToast("Fortschritt zurückgesetzt");
  }

  // --- Passwort ändern (Web-Parität: /api/auth/password-change) ---
  const [pwCur, setPwCur] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwNew2, setPwNew2] = useState("");
  const [pwStatus, setPwStatus] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  async function onPasswordChange(e: Event) {
    e.preventDefault();
    setPwStatus("");
    if (pwNew.length < 8) {
      setPwStatus("Das neue Passwort braucht mindestens 8 Zeichen.");
      return;
    }
    if (pwNew !== pwNew2) {
      setPwStatus("Die neuen Passwörter stimmen nicht überein.");
      return;
    }
    if (pwNew === pwCur) {
      setPwStatus("Das neue Passwort muss sich vom alten unterscheiden.");
      return;
    }
    setPwBusy(true);
    try {
      await endpoints.passwordChange(pwCur, pwNew);
      // Server widerruft alle Sessions → neu anmelden.
      showToast("Passwort geändert — bitte neu anmelden.");
      resetCoach();
      clearSession();
      route("/login", true);
    } catch (err) {
      setPwStatus(err instanceof ApiError ? err.detail : "Passwort konnte nicht geändert werden.");
    } finally {
      setPwBusy(false);
    }
  }

  // --- Account löschen (Sicherheitsdialog, Standard NEIN) ---
  const [delPw, setDelPw] = useState("");
  const [delStatus, setDelStatus] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [delConfirm, setDelConfirm] = useState(false);
  const [delConfirm2, setDelConfirm2] = useState(false); // zweite Sicherheitsstufe (Web-Parität)

  function onDeleteSubmit(e: Event) {
    e.preventDefault();
    setDelStatus("");
    if (!delPw) {
      setDelStatus("Bitte gib zur Bestätigung dein Passwort ein.");
      return;
    }
    setDelConfirm(true); // Sicherheitsfrage vorschalten
  }
  async function onDeleteConfirmed() {
    setDelConfirm(false);
    setDelConfirm2(false);
    setDelBusy(true);
    try {
      await endpoints.accountDelete(delPw);
      resetCoach();
      clearSession();
      route("/login", true);
    } catch (err) {
      setDelStatus(err instanceof ApiError ? err.detail : "Account konnte nicht gelöscht werden.");
    } finally {
      setDelBusy(false);
    }
  }

  const themeLabel =
    themePref.value === "system" ? "System" : themePref.value === "dark" ? "Dunkel" : "Hell";
  const doneQuestions = [...doneKeys.value].filter((k) => k.includes(":question:")).length;
  const displayName = u?.display_name ?? u?.preferred_name ?? "";
  const initials =
    displayName.trim().split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
  const isTrainerRole = ["admin", "trainer", "instructor", "teacher"].includes((u?.role || "").toLowerCase());

  return (
    <div class="stack">
      <BrandHeader center={false} />

      <div class="glass me-card">
        <div class="me">
          <div class="me-av">{initials}</div>
          <div class="me-id">
            <div class="me-nm">{displayName || "—"}</div>
            {u?.email ? <div class="me-em">{u.email}</div> : null}
          </div>
          {u?.role ? (
            <span class={`chip ${isTrainerRole ? "trainer" : "teilnehmer"}`}>{u.role}</span>
          ) : null}
        </div>
        <div class="me-meta">
          {u?.company ? <span>{u.company}</span> : null}
          <span>Abgehakte Fragen · <strong>{doneQuestions}</strong></span>
        </div>
      </div>

      {(() => {
        const cert = certificateStatus.value;
        if (!cert) return null;
        if (cert.goal_reached) {
          return (
            <div class="glass card-pad">
              <div style="font-family:var(--font-head);font-size:18px;color:#fff">
                🎉 Großziel erreicht
              </div>
              <p class="text-muted" style="font-size:13px;margin:8px 0 0">
                Herzlichen Glückwunsch! Deine Abschluss-Urkunde steht bereit
                {cert.certificate?.mail_status === "sent" ? " und ist dir auch per E-Mail zugegangen" : ""}.
              </p>
              <div class="stack" style="margin-top:12px">
                <button class="btn" onClick={onDownloadCertificate}>
                  Urkunde herunterladen
                </button>
              </div>
              {cert.certificate?.id ? (
                <p class="text-muted" style="font-size:12px;margin:8px 0 0">
                  Zertifikats-Nr.: {cert.certificate.id}
                </p>
              ) : null}
            </div>
          );
        }
        return (
          <div class="glass card-pad">
            <div style="font-family:var(--font-head);font-size:16px;color:#fff">
              Weg zum Zertifikat
            </div>
            <div class="stack" style="margin-top:10px;gap:6px">
              {cert.categories.map((c) => (
                <div key={c.key} class="text-muted" style="font-size:13px;display:flex;justify-content:space-between;gap:10px">
                  <span>{c.label}</span>
                  <span style={`color:${c.satisfied ? "var(--teal)" : "inherit"}`}>
                    {c.done}/{c.total} {c.satisfied ? "✓" : `(Ziel: ${c.required})`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      <div class="sec-label">Darstellung</div>
      <div class="grp">
        <div class="seg-row">
          <div class="seg-row-head">
            <span class="row-aa">Aa</span>
            <span class="row-lb">Schriftgröße</span>
          </div>
          <div class="seg" role="group" aria-label="Schriftgröße wählen">
            {FONT_SCALE_ORDER.map((key) => (
              <button
                key={key}
                type="button"
                class={`seg-btn${fontScalePref.value === key ? " active" : ""}`}
                aria-pressed={fontScalePref.value === key}
                aria-label={FONT_SCALE_LABELS[key]}
                title={FONT_SCALE_LABELS[key]}
                onClick={() => setFontScale(key)}
              >
                {FONT_SCALE_SHORT[key]}
              </button>
            ))}
          </div>
        </div>
        <button type="button" class="row row-btn" onClick={cycleTheme}>
          <span class="row-ic"><IconMoon size={18} /></span>
          <span class="row-lb">Thema</span>
          <span class="row-pill">{themeLabel}</span>
        </button>
      </div>

      <div class="sec-label">Konto</div>
      <div class="grp">
        <button type="button" class="row row-btn" onClick={onReset}>
          <span class="row-ic"><IconRefresh size={18} /></span>
          <span class="row-lb">Fortschritt zurücksetzen</span>
          <span class="row-ch"><IconArrowRight size={18} /></span>
        </button>

        <details class="row-fold">
          <summary class="row">
            <span class="row-ic"><IconLock size={18} /></span>
            <span class="row-lb">Passwort ändern</span>
            <span class="row-ch"><IconArrowRight size={18} /></span>
          </summary>
          <form class="row-form" onSubmit={onPasswordChange}>
            <input type="password" autocomplete="current-password" placeholder="Aktuelles Passwort"
              value={pwCur} onInput={(e) => setPwCur((e.target as HTMLInputElement).value)} required />
            <input type="password" autocomplete="new-password" placeholder="Neues Passwort (mind. 8 Zeichen)" minLength={8}
              value={pwNew} onInput={(e) => setPwNew((e.target as HTMLInputElement).value)} required />
            <input type="password" autocomplete="new-password" placeholder="Neues Passwort wiederholen" minLength={8}
              value={pwNew2} onInput={(e) => setPwNew2((e.target as HTMLInputElement).value)} required />
            <button type="submit" class="btn" disabled={pwBusy}>
              {pwBusy ? "Speichert …" : "Passwort speichern"}
            </button>
            {pwStatus ? <p class="settings-form-status" aria-live="polite">{pwStatus}</p> : null}
          </form>
        </details>

        <details class="row-fold danger">
          <summary class="row">
            <span class="row-ic"><IconTrash size={18} /></span>
            <span class="row-lb">Account löschen</span>
            <span class="row-ch"><IconArrowRight size={18} /></span>
          </summary>
          <form class="row-form" onSubmit={onDeleteSubmit}>
            <p class="text-muted" style="font-size:13px;margin:0">
              Das löscht deinen Zugang und deinen Fortschritt <strong>endgültig</strong>. Zur
              Bestätigung gib dein Passwort ein.
            </p>
            <input type="password" autocomplete="current-password" placeholder="Dein Passwort"
              value={delPw} onInput={(e) => setDelPw((e.target as HTMLInputElement).value)} required />
            <button type="submit" class="btn btn-danger" disabled={delBusy}>
              {delBusy ? "Löscht …" : "Account endgültig löschen"}
            </button>
            {delStatus ? <p class="settings-form-status" aria-live="polite">{delStatus}</p> : null}
          </form>
        </details>

        <button type="button" class="row row-btn" onClick={onLogout}>
          <span class="row-ic"><IconLogout size={18} /></span>
          <span class="row-lb">Abmelden</span>
          <span class="row-ch"><IconArrowRight size={18} /></span>
        </button>
      </div>

      {/* Kontingent — im Web über das Topbar-Menü, in der App im Profil. */}
      <QuotaCard />

      {/* Trainer/Admin: Statistik hier eingebettet statt eigener Tab (T3.6). */}
      {isTrainer.value ? <Stats /> : null}

      <p class="text-muted" style="font-size:12.5px;text-align:center">
        Dein Fortschritt wird unter deiner E-Mail gespeichert.
      </p>

      {/* Fortschritt-Reset — Sicherheitsfrage, Standard NEIN (Web-Parität 38c93a8). */}
      {resetConfirm ? (
        <Modal onClose={() => setResetConfirm(false)}>
          <h2>Fortschritt wirklich zurücksetzen?</h2>
          <p>Erledigte Kapitel und Fragen stehen danach wieder auf Anfang.</p>
          <div class="chapter-confirm-actions">
            <button class="btn-coach" onClick={() => setResetConfirm(false)}>Nein</button>
            <button class="btn-ghost btn-danger" onClick={onResetConfirmed}>Ja, zurücksetzen</button>
          </div>
        </Modal>
      ) : null}

      {/* Löschen — ZWEI Sicherheitsfragen hintereinander, je Standard NEIN (Web-Parität 38c93a8). */}
      {delConfirm ? (
        <Modal onClose={() => setDelConfirm(false)}>
          <h2>Account wirklich endgültig löschen?</h2>
          <p>Dein Zugang und dein gesamter Fortschritt werden unwiderruflich gelöscht.</p>
          <div class="chapter-confirm-actions">
            <button class="btn-coach" onClick={() => setDelConfirm(false)}>Nein, behalten</button>
            <button class="btn-ghost btn-danger" onClick={() => { setDelConfirm(false); setDelConfirm2(true); }}>Ja, weiter</button>
          </div>
        </Modal>
      ) : null}
      {delConfirm2 ? (
        <Modal onClose={() => setDelConfirm2(false)}>
          <h2>Letzte Frage: wirklich endgültig löschen?</h2>
          <p>Das lässt sich nicht rückgängig machen.</p>
          <div class="chapter-confirm-actions">
            <button class="btn-coach" onClick={() => setDelConfirm2(false)}>Nein, abbrechen</button>
            <button class="btn-ghost btn-danger" onClick={onDeleteConfirmed}>Ja, endgültig löschen</button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
